import React, { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// components and helpers
import utils from '../../utils/utils';
import Loader from '../Shared/Loader';
import { showNotification } from '../Notifications/NotificationActions';

// mui
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTheme } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import { auditActions } from './AuditActions';
import Button from '@mui/material/Button';
import { AppContext } from '../../containers/AppProviderWrapper';

export interface AuditDrawerProps {
    workspaceId: string | null;
}

const AuditDrawer: React.FC<AuditDrawerProps> = ({ workspaceId }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { currentPath, workspaceList, currentUser } = useContext(AppContext);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [txId, setTxId] = React.useState<string>(null);
    const [pageLimit] = useState<number>(10);
    const [offset, setOffset] = useState<number>(0);
    const [sortField] = useState<string>('lastUpdated');
    const [sortDirection] = useState<string>('DESC');
    const data = useSelector((state: any) => state.audit.activities);
    let { id } = useParams();

    useEffect(() => {
        dispatch(
            auditActions?.getActivities(
                workspaceId,
                pageLimit,
                0,
                sortField,
                sortDirection,
                false
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (data && data.items && data.items.length === data.total) {
            setHasMore(false);
        }
    }, [data]);

    const handleFetchMore = () => {
        const moreOffset = offset + 10;
        setOffset(moreOffset);
        dispatch(
            auditActions?.getActivities(
                workspaceId,
                pageLimit,
                moreOffset,
                sortField,
                sortDirection,
                true
            )
        );
    };

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, id) => {
        setTxId(id || null);
    };

    const handlePopoverClose = () => {
        setTxId(null);
    };

    const handleCopyTx = (id) => {
        navigator.clipboard.writeText(id);
        dispatch(
            showNotification(
                'Transaction ID successfully copied to clipboard.',
                'success'
            )
        );
    };

    const handleAllActivities = () => {
        if (id) {
            navigate(`${currentPath}/activities`);
        } else {
            navigate('/activities');
        }
    };

    const createCard = (item) => {
        let workspaceName =
            workspaceList &&
            workspaceList.items.find((o) => o.id === item.workspaceId);
        if (workspaceName) {
            workspaceName = workspaceName.name;
        } else {
            workspaceName = item.workspaceId;
        }
        return (
            <Grid container direction={'row'}>
                <Grid item xs={2}>
                    {item.collectionName.includes('File') ||
                    item.collectionName.includes('files') ? (
                        <InsertDriveFileIcon color={'primary'} />
                    ) : item.collectionName.includes('accesses') ? (
                        <AdminPanelSettingsIcon color={'primary'} />
                    ) : item.collectionName.includes('cases') ? (
                        <SwitchAccountIcon color={'primary'} />
                    ) : (
                        <FolderIcon color={'primary'} />
                    )}
                </Grid>
                <Grid item xs={10}>
                    <Typography
                        variant={'body2'}
                        color={'primary'}
                        style={{ overflow: 'hidden' }}
                    >
                        <b>
                            {utils.toTitleText(item.action.toLowerCase())}{' '}
                            {item.collectionName
                                .replace('-', ' ')
                                .replace('hde', 'HDE')}
                        </b>
                    </Typography>
                    {location.pathname === '/' && (
                        <div>
                            <Typography
                                variant={'caption'}
                                style={{ overflow: 'hidden' }}
                            >
                                {' '}
                                <b>Workspace</b>: {workspaceName}
                            </Typography>
                            <br />
                        </div>
                    )}
                    {item.collectionName === 'hde-cases' &&
                        item &&
                        item.entityId && (
                            <div>
                                <Typography
                                    variant={'caption'}
                                    style={{ overflow: 'hidden' }}
                                >
                                    {' '}
                                    <b>Case ID</b>:{' '}
                                    {item.entityId.includes('/')
                                        ? item.entityId.split('/')[1]
                                        : item.entityId}
                                </Typography>
                                <br />
                            </div>
                        )}
                    {currentUser &&
                    currentUser.userConfiguration &&
                    currentUser.userConfiguration.beta ? (
                        <Typography variant={'caption'}>
                            <b>User</b>:{' '}
                            <span style={{ filter: 'blur(3px)' }}>
                                {'betaTester@test.com'}
                            </span>
                        </Typography>
                    ) : (
                        <Typography variant={'caption'}>
                            <b>User</b>: {item.userId || '---'}
                        </Typography>
                    )}
                    <br />
                    <Typography variant={'caption'}>
                        <b>Modified</b>:{' '}
                        {utils.formatDate(item.lastUpdated || '---', 'L LTS')}
                    </Typography>
                    <br />
                    {item.transactionId && (
                        <Typography
                            variant={'caption'}
                            onMouseEnter={(e) =>
                                handlePopoverOpen(e, item.transactionId)
                            }
                            onMouseLeave={handlePopoverClose}
                            sx={{
                                color: 'gray',
                                '&:hover': { cursor: 'copy' },
                            }}
                            onClick={() => handleCopyTx(item.transactionId)}
                        >
                            {txId && txId === item.transactionId
                                ? txId
                                : 'Secure Transaction ID'}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        );
    };

    return (
        // @ts-ignore
        <Paper sx={{ padding: 2, marginLeft: 2 }} elevation={0}>
            <Grid
                container
                justifyContent={'space-between'}
                alignItems={'flex-start'}
            >
                <Grid item>
                    <Typography variant={'h6'} sx={{ mb: 2 }}>
                        Activity Log
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        size={'small'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={handleAllActivities}
                    >
                        View All
                    </Button>
                </Grid>
            </Grid>
            <InfiniteScroll
                dataLength={data && data.items && data.items.length}
                next={handleFetchMore}
                hasMore={hasMore}
                loader={
                    <Grid
                        container
                        justifyContent={'center'}
                        style={{
                            marginTop: 5,
                            marginBottom: '5vh',
                        }}
                        direction={'column'}
                        alignItems={'center'}
                        alignContent={'center'}
                    >
                        <Grid item>
                            <Loader color={'#60CEBD'} size={50} />
                        </Grid>
                    </Grid>
                }
                height={'80vh'}
                endMessage={
                    <Typography
                        variant={'caption'}
                        color={theme.palette.text.secondary}
                        style={{ textAlign: 'center' }}
                    >
                        {data && data.items && data.items.length
                            ? `You've reached the beginning of activity tracking`
                            : null}
                    </Typography>
                }
            >
                {data && data.items.length ? (
                    data.items.map((item, i) => {
                        return (
                            <div key={`activity-${i}`}>
                                {createCard(item)}
                                <Divider sx={{ m: 2 }} />
                            </div>
                        );
                    })
                ) : (
                    <Typography
                        variant={'caption'}
                        color={theme.palette.text.secondary}
                    >
                        No activity recorded
                    </Typography>
                )}
            </InfiniteScroll>
        </Paper>
    );
};

export default AuditDrawer;
